import { logger } from '@adornis/base/logging.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MailTemplateType } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import { mmr } from '@adornis/bh-matching-mails/mail-registry.js';
import { addCommentOnMatch, Match, MatchComment } from '@adornis/bh-matching-matches/entities/match.js';
import { getUserClasses } from '@adornis/bh-matching-matches/user-class.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { merge, switchMap } from 'rxjs';
import './auto-matching.js';

// ==================== create ===================
const createMatches = registerMutation({
  type: () => [String],
  operationName: 'createMatches',
  params: [{ type: () => [Match], name: 'matches' }],
  resolve: (matches: Match[]) => {
    return async () => {
      for (const match of matches) {
        match.comments = [new MatchComment({ text: 'Match wurde erstellt', createdAt: new Date() })];
        await match.create();
      }
      return matches.map(m => m._id);
    };
  },
  options: {},
});

// ==================== reminder mails ===================
const sendReminderMailForMatch = registerMutation({
  operationName: 'sendReminderMailForMatch',
  resolve: (matchID, userID) => async () => {
    const match = await getByID(Match, matchID)({ mentorID: 1, menteeID: 1 });
    if (!match) throw new Error('No match found for ID ' + matchID);
    if (!match.mentorID || !match.menteeID)
      throw new Error(
        `Incomplete match found (matchID: ${matchID}; mentorID: ${match.mentorID}; menteeID: ${match.menteeID})`,
      );
    if (match.mentorID !== userID && match.menteeID !== userID)
      throw new Error(`UserID is not part of this match (userID: ${userID}; matchID: ${matchID})`);

    const userClasses = getUserClasses();

    const mentor = await getByID(userClasses.mentor, match.mentorID)(selectionSet(() => userClasses.mentor, 2));
    const mentee = await getByID(userClasses.mentee, match.menteeID)(selectionSet(() => userClasses.mentee, 2));
    if (!mentor || !mentee)
      throw new Error(
        `Couldnt find mentor or mentee for match (mentor: ${!!mentor}; mentee: ${!!mentee}; matchID: ${matchID})`,
      );

    const isMentor = match.mentorID === userID;

    const recipients = [new Recipient(isMentor ? mentor.email : mentee.email, undefined, undefined)];
    const options = { recipients, mailer: await mailer };
    const template = isMentor
      ? mmr.contactReminderMentor(matchID, MailTemplateType.CONTACT_REMINDER_MENTOR)
      : mmr.contactReminderMentee(matchID, MailTemplateType.CONTACT_REMINDER_MENTEE);

    await template.send(options);

    await addCommentOnMatch(matchID, `Erinnerungsmail wurde versandt für ${isMentor ? 'Mentor' : 'Mentee'}`)().catch(
      err => {
        logger.error({ err });
        // no further consequences, this is just an information
      },
    );
  },
  type: () => Boolean,
  params: [
    { name: 'matchID', type: () => ID },
    { name: 'userID', type: () => ID },
  ],
});

const _filterAndOptionallyPaginateMatches = async (
  filter: AdornisFilter,
  skip?: number,
  limit?: number,
  timeframeID?: string,
) => {
  return (await getRawCollection<Match>(Match._collectionName))
    .aggregate<Match>(
      [
        ...(timeframeID ? [{ $match: { timeframeID } }] : []),
        {
          $lookup: { from: BuddyUser._collectionName, as: 'mentor', localField: 'mentorID', foreignField: '_id' },
        },
        { $set: { mentor: { $arrayElemAt: ['$mentor', 0] } } },
        {
          $lookup: { from: BuddyUser._collectionName, as: 'mentee', localField: 'menteeID', foreignField: '_id' },
        },
        { $set: { mentee: { $arrayElemAt: ['$mentee', 0] } } },
        {
          $lookup: {
            from: BuddyUser._collectionName,
            as: 'mentee.preferedMentors',
            localField: 'mentee.preferedMentorsIDs',
            foreignField: '_id',
          },
        },
        { $match: filter.getMongoFilter() },

        { $sort: filter.getMongoOptions().sort },
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ],
      {
        collation: { locale: 'en', strength: 1 },
      },
    )
    .toArray();
};
const getMatchesFiltered = registerQuery({
  operationName: 'getMatchesFiltered',
  resolve:
    (timeframeID: string, filter?: AdornisFilter) =>
    async <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      await _filterAndOptionallyPaginateMatches(filter ?? new AdornisFilter({}), undefined, undefined, timeframeID),
  type: () => [Match],
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const subscribeMatchesPaginatedAndFiltered = registerSubscription({
  operationName: 'subscribeMatchesPaginatedAndFiltered',
  resolve:
    (skip: number, limit: number, filter: AdornisFilter, timeframeID?: string) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) => {
      return merge(
        watchQuery(Match._collectionName, {}, { projection: { _id: 1 } }),
        watchQuery(BuddyUser._collectionName, {}, { projection: { _id: 1 } }),
      ).pipe(switchMap(async () => _filterAndOptionallyPaginateMatches(filter, skip, limit, timeframeID)));
    },
  type: () => [Match],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'timeframeID', type: () => ID },
  ],
});

const getMailAddressesOfMatches = registerQuery({
  operationName: 'getMailAddressesOfMatches',
  resolve: (timeframeID: string, filter: AdornisFilter, userType?: 'mentee' | 'mentor') => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);

    if (!userType) return matches.flatMap(match => [match.mentor?.email, match.mentee?.email]).filter(Boolean);
    if (userType === 'mentee') return matches.map(match => match.mentee?.email).filter(Boolean);
    if (userType === 'mentor') return matches.map(match => match.mentor?.email).filter(Boolean);
  },
  type: () => [String],
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'userClass', type: () => String },
  ],
});

const getUserIDsOfMatches = registerQuery({
  operationName: 'getUserIDsOfMatches',
  resolve: (timeframeID: string, filter: AdornisFilter, userType?: 'mentee' | 'mentor') => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);

    if (!userType) return matches.flatMap(match => [match.mentor?._id, match.mentee?._id]).filter(Boolean) as string[];
    if (userType === 'mentee') return matches.map(match => match.mentee?._id).filter(Boolean) as string[];
    if (userType === 'mentor') return matches.map(match => match.mentor?._id).filter(Boolean) as string[];
  },
  type: () => [String],
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'userClass', type: () => String },
  ],
});

const getIDsOfMatches = registerQuery({
  operationName: 'getIDsOfMatches',
  type: () => [String],
  resolve: (timeframeID: string, filter: AdornisFilter) => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);
    return matches.map(match => match._id);
  },
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const _getAllEmailAddressFromMatch = (curMatch: Match, userType?: typeof BuddyUserMentee | typeof BuddyUserMentor) => {
  let allMails = new Array<string>();

  if (!userType || userType._class === BuddyUserMentee._class)
    if (curMatch.mentee?.email) allMails.push(curMatch.mentee?.email);

  if (!userType || userType._class === BuddyUserMentor._class)
    if (curMatch.mentor?.email) allMails.push(curMatch.mentor?.email);

  return allMails;
};

const _getAllEmailAddressFromMatches = (
  matches: Match[],
  userType?: typeof BuddyUserMentee | typeof BuddyUserMentor,
) => {
  return matches.flatMap(match => _getAllEmailAddressFromMatch(match, userType));
};

// ==================== namespace ===================
export namespace MatchAPI {
  /**
   * @returns id array of created matches
   */

  export const create = createMatches;
  export const sendReminderMail = sendReminderMailForMatch;
  export const subscribePaginatedAndFiltered = subscribeMatchesPaginatedAndFiltered;
  export const getMailAddresses = getMailAddressesOfMatches;
  export const getUserIDs = getUserIDsOfMatches;
  export const getMatchesIDs = getIDsOfMatches;
  export const getAllEmailAddressFromMatch = _getAllEmailAddressFromMatch;
  export const getAllEmailAddressFromMatches = _getAllEmailAddressFromMatches;
  export const getFiltered = getMatchesFiltered;
}
